import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Grid,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  DashboardOutlined,
  DollarOutlined,
  FallOutlined,
  FileTextOutlined,
  ReloadOutlined,
  RiseOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs, { Dayjs } from "dayjs";
import { useLogisticsDashboard } from "../hooks/useLogisticsDashboard";
import InvoiceStatusBadge from "../components/InvoiceStatusBadge";
import "./LogisticsDashboard.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

type RangePresetKey = "30d" | "90d" | "12m" | "ytd" | "custom";

const transportModeLabels: Record<string, string> = {
  sea: "Maritime",
  air: "Aerien",
  road: "Routier",
  rail: "Ferroviaire",
  multimodal: "Multimodal",
};

const statusLabels: Record<string, string> = {
  draft: "Brouillon",
  confirmed: "Confirme",
  in_transit: "En transit",
  delivered: "Livre",
  completed: "Termine",
  cancelled: "Annule",
};

const pieColors = ["#1d4ed8", "#0f766e", "#f59e0b", "#dc2626", "#7c3aed"];

const formatNumber = (value: number | string | null | undefined) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numericValue)
    : "0";
};

const formatCurrency = (
  value: number | string | null | undefined,
  currencySymbol?: string | null
) => {
  const formatted = formatNumber(value);
  return currencySymbol ? `${formatted} ${currencySymbol}` : formatted;
};

const formatPercentage = (value: number | string | null | undefined) => {
  const numericValue = Number(value);
  return `${Number.isFinite(numericValue) ? numericValue : 0}.0`.replace(
    /(\d+\.\d).*/,
    "$1"
  ) + "%";
};

const getRangeFromPreset = (preset: RangePresetKey): [Dayjs, Dayjs] => {
  const end = dayjs();
  if (preset === "30d") return [end.subtract(29, "day"), end];
  if (preset === "90d") return [end.subtract(89, "day"), end];
  if (preset === "ytd") return [end.startOf("year"), end];
  return [end.subtract(11, "month"), end];
};

const LogisticsDashboard: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [activePreset, setActivePreset] = useState<RangePresetKey>("12m");
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(
    getRangeFromPreset("12m")
  );
  const {
    loading,
    kpis,
    revenueCost,
    profitability,
    outstanding,
    transportModes,
    fetchDashboard,
    selectedCompany,
  } = useLogisticsDashboard();

  useEffect(() => {
    fetchDashboard({ startDate: dateRange[0], endDate: dateRange[1] });
  }, [dateRange, fetchDashboard]);

  const currencySymbol = kpis?.currency_symbol || "";

  const chartData = useMemo(() => {
    const dataMap = new Map(revenueCost.map((item) => [item.month, item]));
    const filled: any[] = [];
    let cursor = dateRange[0].startOf("month");
    const end = dateRange[1].startOf("month");

    while (cursor.isBefore(end) || cursor.isSame(end, "month")) {
      const key = cursor.format("YYYY-MM");
      const entry = dataMap.get(key);
      filled.push({
        month: cursor.format("MMM YYYY"),
        total_revenue: Number(entry?.total_revenue || 0),
        total_cost: Number(entry?.total_cost || 0),
        total_profit: Number(entry?.total_profit || 0),
      });
      cursor = cursor.add(1, "month");
    }

    return filled;
  }, [dateRange, revenueCost]);

  const transportData = useMemo(
    () =>
      transportModes.map((item) => ({
        name: transportModeLabels[item.transport_mode] || item.transport_mode,
        value: Number(item.shipment_count || 0),
        margin: Number(item.avg_profit_margin_percentage || 0),
      })),
    [transportModes]
  );

  const kpiCards = [
    {
      key: "active",
      title: "Dossiers actifs",
      value: Number(kpis?.active_shipments || 0),
      icon: <FileTextOutlined />,
    },
    {
      key: "revenue",
      title: "Chiffre d'affaires",
      value: formatCurrency(kpis?.total_revenue, currencySymbol),
      icon: <RiseOutlined />,
    },
    {
      key: "cost",
      title: "Couts totaux",
      value: formatCurrency(kpis?.total_cost, currencySymbol),
      icon: <FallOutlined />,
    },
    {
      key: "profit",
      title: "Marge totale",
      value: formatCurrency(kpis?.total_profit, currencySymbol),
      icon: <DollarOutlined />,
    },
    {
      key: "receivables",
      title: "Creances clients",
      value: formatCurrency(kpis?.outstanding_receivables, currencySymbol),
      icon: <SwapOutlined />,
    },
    {
      key: "payables",
      title: "Dettes fournisseurs",
      value: formatCurrency(kpis?.outstanding_payables, currencySymbol),
      icon: <SwapOutlined />,
    },
  ];

  const profitabilityColumns = [
    {
      title: "Dossier",
      dataIndex: "shipment_number",
      key: "shipment_number",
      render: (value: string) => <Text strong>{value || "-"}</Text>,
    },
    { title: "Client", dataIndex: "client_name", key: "client_name" },
    {
      title: "Marge",
      dataIndex: "profit_margin",
      key: "profit_margin",
      align: "right" as const,
      render: (value: number) => formatCurrency(value, currencySymbol),
    },
    {
      title: "Marge %",
      dataIndex: "profit_margin_percentage",
      key: "profit_margin_percentage",
      align: "right" as const,
      render: (value: number) => formatPercentage(value),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <Tag color={value === "completed" ? "green" : "default"}>
          {statusLabels[value] || value || "-"}
        </Tag>
      ),
    },
  ];

  const invoiceColumns = [
    {
      title: "Facture",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (value: string) => <Text strong>{value}</Text>,
    },
    { title: "Client", dataIndex: "client_name", key: "client_name" },
    {
      title: "Solde",
      dataIndex: "balance_due",
      key: "balance_due",
      align: "right" as const,
      render: (value: number, record: any) =>
        formatCurrency(value, record.currency_symbol || currencySymbol),
    },
    {
      title: "Echeance",
      dataIndex: "due_date",
      key: "due_date",
      render: (value: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Statut",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (value: string) => <InvoiceStatusBadge status={value as any} size="small" />,
    },
  ];

  const supplierBillColumns = [
    {
      title: "Facture",
      dataIndex: "bill_number",
      key: "bill_number",
      render: (value: string) => <Text strong>{value}</Text>,
    },
    { title: "Fournisseur", dataIndex: "supplier_name", key: "supplier_name" },
    {
      title: "Solde",
      dataIndex: "balance_due",
      key: "balance_due",
      align: "right" as const,
      render: (value: number, record: any) =>
        formatCurrency(value, record.currency_symbol || currencySymbol),
    },
    {
      title: "Echeance",
      dataIndex: "due_date",
      key: "due_date",
      render: (value: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Statut",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (value: string) => <InvoiceStatusBadge status={value as any} size="small" />,
    },
  ];

  if (!selectedCompany) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour afficher le dashboard logistique."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  const profitList = (title: string, items: any[], emptyText: string) => (
    <Card className="logistics-dashboard__panel" title={title} loading={loading}>
      {items.length === 0 ? (
        <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="logistics-dashboard__list">
          {items.slice(0, 4).map((item) => (
            <div className="logistics-dashboard__list-card" key={item.shipment_id}>
              <div className="logistics-dashboard__list-row">
                <div>
                  <Text strong>{item.shipment_number || "-"}</Text>
                  <Text type="secondary">{item.client_name || "Client non renseigne"}</Text>
                </div>
                <Tag color={item.status === "completed" ? "green" : "default"}>
                  {statusLabels[item.status] || item.status || "-"}
                </Tag>
              </div>
              <div className="logistics-dashboard__list-row logistics-dashboard__list-row--metric">
                <div>
                  <Text type="secondary">Marge</Text>
                  <Text strong>{formatCurrency(item.profit_margin, currencySymbol)}</Text>
                </div>
                <div>
                  <Text type="secondary">Marge %</Text>
                  <Text strong>{formatPercentage(item.profit_margin_percentage)}</Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  const outstandingCards = (items: any[], numberKey: string, nameKey: string, emptyText: string) =>
    items.length === 0 ? (
      <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
    ) : (
      <div className="logistics-dashboard__list">
        {items.slice(0, 5).map((item) => (
          <div className="logistics-dashboard__list-card" key={item.id}>
            <div className="logistics-dashboard__list-row">
              <div>
                <Text strong>{item[numberKey] || "-"}</Text>
                <Text type="secondary">{item[nameKey] || "-"}</Text>
              </div>
              <InvoiceStatusBadge status={item.payment_status as any} size="small" />
            </div>
            <div className="logistics-dashboard__list-row logistics-dashboard__list-row--metric">
              <div>
                <Text type="secondary">Solde</Text>
                <Text strong>{formatCurrency(item.balance_due, item.currency_symbol || currencySymbol)}</Text>
              </div>
              <div>
                <Text type="secondary">Echeance</Text>
                <Text strong>{item.due_date ? dayjs(item.due_date).format("DD/MM/YYYY") : "-"}</Text>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

  return (
    <div className="logistics-dashboard">
      <section className="logistics-dashboard__hero">
        <div>
          <Text className="logistics-dashboard__hero-kicker">Pilotage logistique</Text>
          <Title level={isMobile ? 3 : 2}>Tableau de bord logistique</Title>
          <Text className="logistics-dashboard__hero-text">
            Vue synthese des dossiers, marges et encours pour garder les flux rentables et les paiements sous controle.
          </Text>
        </div>
        <div className="logistics-dashboard__hero-stats">
          <div><span>Dossiers actifs</span><strong>{formatNumber(kpis?.active_shipments)}</strong></div>
          <div><span>Marge totale</span><strong>{formatCurrency(kpis?.total_profit, currencySymbol)}</strong></div>
          <div><span>Rentabilite moyenne</span><strong>{formatPercentage(kpis?.average_profit_margin_percentage)}</strong></div>
        </div>
      </section>

      <Card className="logistics-dashboard__panel">
        <div className="logistics-dashboard__toolbar">
          <Segmented
            block={isMobile}
            value={activePreset}
            onChange={(value) => {
              const preset = value as RangePresetKey;
              setActivePreset(preset);
              if (preset !== "custom") setDateRange(getRangeFromPreset(preset));
            }}
            options={[
              { label: "30 j", value: "30d" },
              { label: "90 j", value: "90d" },
              { label: "12 mois", value: "12m" },
              { label: "YTD", value: "ytd" },
              { label: "Perso", value: "custom" },
            ]}
          />
          <Space className="logistics-dashboard__toolbar-actions">
            <RangePicker
              value={dateRange}
              onChange={(range) => {
                if (!range || !range[0] || !range[1]) return;
                setActivePreset("custom");
                setDateRange([range[0], range[1]]);
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchDashboard({ startDate: dateRange[0], endDate: dateRange[1] })} loading={loading}>
              Actualiser
            </Button>
          </Space>
        </div>
      </Card>

      {isMobile ? (
        <>
          <div className="logistics-dashboard__kpi-grid">
            {kpiCards.map((card) => (
              <Card className="logistics-dashboard__kpi-card" key={card.key} loading={loading}>
                <div className="logistics-dashboard__kpi-title">{card.icon}<span>{card.title}</span></div>
                <strong>{card.value}</strong>
              </Card>
            ))}
          </div>

          <Card className="logistics-dashboard__panel" title="Revenus, couts et marge" loading={loading}>
            {chartData.length === 0 ? <Empty description="Aucune donnee pour la periode choisie" /> : (
              <>
                <div className="logistics-dashboard__chart logistics-dashboard__chart--line">
                  <ResponsiveContainer>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis width={56} tick={{ fontSize: 11 }} tickFormatter={(value) => formatNumber(value)} />
                      <RechartsTooltip formatter={(value: any) => formatCurrency(value, currencySymbol)} />
                      <Line type="monotone" dataKey="total_revenue" stroke="#16a34a" strokeWidth={2.4} dot={false} />
                      <Line type="monotone" dataKey="total_cost" stroke="#f59e0b" strokeWidth={2.2} dot={false} />
                      <Line type="monotone" dataKey="total_profit" stroke="#2563eb" strokeWidth={2.2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="logistics-dashboard__legend">
                  <span><i style={{ background: "#16a34a" }} />Revenus</span>
                  <span><i style={{ background: "#f59e0b" }} />Couts</span>
                  <span><i style={{ background: "#2563eb" }} />Marge</span>
                </div>
              </>
            )}
          </Card>

          <Card className="logistics-dashboard__panel" title="Modes de transport" loading={loading}>
            {transportData.length === 0 ? <Empty description="Aucune donnee" /> : (
              <>
                <div className="logistics-dashboard__chart logistics-dashboard__chart--pie">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={transportData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                        {transportData.map((entry, index) => <Cell key={`${entry.name}-${index}`} fill={pieColors[index % pieColors.length]} />)}
                      </Pie>
                      <RechartsTooltip formatter={(value: any, name: any) => [`${formatNumber(value)} dossiers`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="logistics-dashboard__list">
                  {transportData.map((item, index) => (
                    <div className="logistics-dashboard__transport-row" key={item.name}>
                      <div><span className="logistics-dashboard__transport-dot" style={{ background: pieColors[index % pieColors.length] }} />{item.name}</div>
                      <Text strong>{formatNumber(item.value)} dossiers</Text>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          {profitList("Top dossiers rentables", profitability.top || [], "Aucun dossier rentable")}
          {profitList("Dossiers deficitaires", profitability.loss || [], "Aucun dossier deficitaire")}

          <Card className="logistics-dashboard__panel" title="Encours" loading={loading}>
            <Tabs
              items={[
                { key: "receivables", label: "Creances", children: outstandingCards(outstanding.invoices || [], "invoice_number", "client_name", "Aucune creance en cours") },
                { key: "payables", label: "Dettes", children: outstandingCards(outstanding.supplierBills || [], "bill_number", "supplier_name", "Aucune dette en cours") },
              ]}
            />
          </Card>
        </>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {kpiCards.map((card) => (
              <Col xs={24} sm={12} md={8} lg={8} xl={4} key={card.key}>
                <Card className="logistics-dashboard__panel" loading={loading}>
                  <Statistic title={<Space size={8}>{card.icon}<span>{card.title}</span></Space>} value={card.value as any} />
                </Card>
              </Col>
            ))}
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card className="logistics-dashboard__panel" title={<Space><DashboardOutlined />Revenus vs Couts</Space>} loading={loading}>
                {chartData.length === 0 ? <Empty description="Aucune donnee pour la periode choisie" /> : (
                  <div className="logistics-dashboard__chart logistics-dashboard__chart--desktop-line">
                    <ResponsiveContainer>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(value) => formatNumber(value)} />
                        <RechartsTooltip formatter={(value: any) => formatCurrency(value, currencySymbol)} />
                        <Legend />
                        <Line type="monotone" dataKey="total_revenue" name="Revenus" stroke="#16a34a" strokeWidth={2.4} />
                        <Line type="monotone" dataKey="total_cost" name="Couts" stroke="#f59e0b" strokeWidth={2.2} />
                        <Line type="monotone" dataKey="total_profit" name="Marge" stroke="#2563eb" strokeWidth={2.2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card className="logistics-dashboard__panel" title="Modes de transport" loading={loading}>
                {transportData.length === 0 ? <Empty description="Aucune donnee" /> : (
                  <div className="logistics-dashboard__chart logistics-dashboard__chart--desktop-pie">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={transportData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                          {transportData.map((entry, index) => <Cell key={`${entry.name}-${index}`} fill={pieColors[index % pieColors.length]} />)}
                        </Pie>
                        <RechartsTooltip formatter={(value: any, name: any) => [`${formatNumber(value)} dossiers`, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card className="logistics-dashboard__panel" title="Top dossiers rentables" loading={loading}>
                <Table dataSource={profitability.top || []} columns={profitabilityColumns} rowKey="shipment_id" pagination={false} size="small" locale={{ emptyText: <Empty description="Aucun dossier rentable" /> }} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="logistics-dashboard__panel" title="Dossiers deficitaires" loading={loading}>
                <Table dataSource={profitability.loss || []} columns={profitabilityColumns} rowKey="shipment_id" pagination={false} size="small" locale={{ emptyText: <Empty description="Aucun dossier deficitaire" /> }} />
              </Card>
            </Col>
          </Row>
          <Card className="logistics-dashboard__panel" title="Encours" loading={loading}>
            <Tabs
              items={[
                { key: "receivables", label: "Creances clients", children: <Table dataSource={outstanding.invoices || []} columns={invoiceColumns} rowKey="id" pagination={false} size="small" locale={{ emptyText: <Empty description="Aucune creance en cours" /> }} /> },
                { key: "payables", label: "Dettes fournisseurs", children: <Table dataSource={outstanding.supplierBills || []} columns={supplierBillColumns} rowKey="id" pagination={false} size="small" locale={{ emptyText: <Empty description="Aucune dette en cours" /> }} /> },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default LogisticsDashboard;
